"use client";

import {
	resolveLucideAnimatedPlayOn,
	sanitizeAnimationHostDomId,
} from "@multi-tenant-cms/builder/runtime/lucideAnimatedPalette";
import {
	type ComponentType,
	type CSSProperties,
	type RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { createRoot, type Root } from "react-dom/client";

type AnimatedHandle = {
	startAnimation?: () => void;
	stopAnimation?: () => void;
};

function applySvgStrokeWidth(root: HTMLElement | null, strokeWidth: number) {
	if (!root) {
		return;
	}
	const svg = root.querySelector("svg");
	if (!(svg instanceof SVGSVGElement)) {
		return;
	}
	svg.setAttribute("stroke-width", String(strokeWidth));
}

function isLikelyAnimatedIconExport(value: unknown): boolean {
	if (typeof value === "function") {
		return true;
	}
	if (typeof value !== "object" || value === null) {
		return false;
	}
	return (
		"$$typeof" in value &&
		"render" in value &&
		typeof (value as { render?: unknown }).render === "function"
	);
}

type HydratedLucideLiveProps = {
	Cmp: ComponentType<{
		size?: number;
		style?: CSSProperties;
		ref?: RefObject<AnimatedHandle | null>;
	}>;
	sizePx: number;
	color: string;
	strokeW: number;
	playOn: "hover" | "click";
	hostId?: string;
	/** Hover/click on nearest `<a href>` / `<button>` (button-embedded icons). */
	delegateToParentButton?: boolean;
	/**
	 * When set (incl. `null`), skip `wrapRef` lookup: host resolved from the live
	 * mount `span` before `createRoot` so binding works on first paint (region /
	 * preview SSR).
	 */
	preboundButtonOrLinkHost?: HTMLElement | null;
};

function HydratedLucideLive({
	Cmp,
	sizePx,
	color,
	strokeW,
	playOn,
	hostId,
	delegateToParentButton = false,
	preboundButtonOrLinkHost,
}: HydratedLucideLiveProps) {
	const iconRef = useRef<AnimatedHandle | null>(null);
	const wrapRef = useRef<HTMLDivElement | null>(null);
	const clickPlayingRef = useRef(false);
	const [hostListenersBound, setHostListenersBound] = useState(false);

	const runStop = useCallback(() => {
		iconRef.current?.stopAnimation?.();
		clickPlayingRef.current = false;
	}, []);

	const runStart = useCallback(() => {
		iconRef.current?.startAnimation?.();
		clickPlayingRef.current = true;
	}, []);

	const bindToExplicitHost = Boolean(hostId);
	const bindToParentButton = delegateToParentButton && !hostId;
	const useExternalInteractionHost = bindToExplicitHost || bindToParentButton;

	useEffect(() => {
		clickPlayingRef.current = false;
		setHostListenersBound(false);
	}, [delegateToParentButton, hostId, playOn, preboundButtonOrLinkHost]);

	useEffect(() => {
		if (!useExternalInteractionHost) {
			setHostListenersBound(false);
			return;
		}

		let cancelled = false;
		let rafId = 0;
		let attempts = 0;
		let hostEl: HTMLElement | null = null;

		const onEnter = () => {
			iconRef.current?.startAnimation?.();
		};
		const onLeave = () => {
			iconRef.current?.stopAnimation?.();
		};
		const onHostClick = (ev: MouseEvent) => {
			ev.preventDefault();
			if (clickPlayingRef.current) {
				runStop();
			} else {
				runStart();
			}
		};

		const bind = (h: HTMLElement) => {
			hostEl = h;
			if (playOn === "hover") {
				h.addEventListener("pointerenter", onEnter);
				h.addEventListener("pointerleave", onLeave);
			} else {
				h.addEventListener("click", onHostClick);
			}
			if (!cancelled) {
				setHostListenersBound(true);
			}
		};

		const unbind = () => {
			if (!hostEl) {
				return;
			}
			if (playOn === "hover") {
				hostEl.removeEventListener("pointerenter", onEnter);
				hostEl.removeEventListener("pointerleave", onLeave);
			} else {
				hostEl.removeEventListener("click", onHostClick);
			}
			hostEl = null;
			setHostListenersBound(false);
		};

		if (bindToExplicitHost && hostId) {
			const findHost = () => {
				if (cancelled) {
					return;
				}
				const h = document.getElementById(hostId);
				if (h) {
					bind(h);
					return;
				}
				attempts += 1;
				if (attempts > 90) {
					return;
				}
				rafId = requestAnimationFrame(findHost);
			};

			findHost();
		} else if (preboundButtonOrLinkHost === undefined) {
			const findParentButton = () => {
				if (cancelled) {
					return;
				}
				const w = wrapRef.current;
				if (!w) {
					attempts += 1;
					if (attempts > 90) {
						return;
					}
					rafId = requestAnimationFrame(findParentButton);
					return;
				}
				const b = w.closest("a, button");
				if (b instanceof HTMLElement) {
					bind(b);
					return;
				}
				attempts += 1;
				if (attempts > 90) {
					return;
				}
				rafId = requestAnimationFrame(findParentButton);
			};

			findParentButton();
		} else if (preboundButtonOrLinkHost) {
			bind(preboundButtonOrLinkHost);
		}

		return () => {
			cancelled = true;
			cancelAnimationFrame(rafId);
			unbind();
		};
	}, [
		bindToExplicitHost,
		bindToParentButton,
		hostId,
		playOn,
		preboundButtonOrLinkHost,
		runStart,
		runStop,
		useExternalInteractionHost,
	]);

	useLayoutEffect(() => {
		applySvgStrokeWidth(wrapRef.current, strokeW);
	}, [Cmp, strokeW, sizePx]);

	const useHostOnly = useExternalInteractionHost && hostListenersBound;

	const handlers = useHostOnly
		? {}
		: playOn === "hover"
			? {
					onPointerEnter: () => {
						iconRef.current?.startAnimation?.();
					},
					onPointerLeave: () => {
						iconRef.current?.stopAnimation?.();
					},
				}
			: {
					onClick: () => {
						if (clickPlayingRef.current) {
							runStop();
						} else {
							runStart();
						}
					},
				};

	const px = Math.max(12, Math.round(sizePx));

	return (
		<div
			ref={wrapRef}
			{...handlers}
			style={{
				cursor: !useHostOnly && playOn === "click" ? "pointer" : undefined,
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				width: sizePx,
				height: sizePx,
				lineHeight: 0,
			}}
		>
			<Cmp
				ref={iconRef}
				size={px}
				style={{ color, display: "block", lineHeight: 0 }}
			/>
		</div>
	);
}

function readMountProps(el: HTMLElement): {
	iconId: string;
	sizePx: number;
	color: string;
	strokeW: number;
	playOn: "hover" | "click";
	hostId?: string;
	delegateToParentButton: boolean;
} {
	const iconId = el.dataset.pbLucideIconId?.trim() || "SparklesIcon";
	const sizeRaw = Number.parseInt(el.dataset.pbLucideSize ?? "24", 10);
	const sizePx = Number.isFinite(sizeRaw) && sizeRaw > 0 ? sizeRaw : 24;
	const strokeRaw = Number.parseFloat(el.dataset.pbLucideStroke ?? "2");
	const strokeW =
		Number.isFinite(strokeRaw) && strokeRaw > 0
			? Math.min(4, Math.max(0.5, strokeRaw))
			: 2;
	const playOn = resolveLucideAnimatedPlayOn(el.dataset.pbLucidePlay);
	const color = el.dataset.pbLucideColor?.trim() || "#0f172a";
	const hostRaw = el.dataset.pbLucideAnimationHost?.trim();
	const hostId = sanitizeAnimationHostDomId(hostRaw);
	const delegateToParentButton =
		el.dataset.pbLucideDelegateButton === "1" ||
		el.getAttribute("data-pb-lucide-delegate-button") === "1";
	return {
		iconId,
		sizePx,
		color,
		strokeW,
		playOn,
		hostId,
		delegateToParentButton,
	};
}

/**
 * One-time client hydration for library Lucide animated icons on public pages
 * (`BuilderPreview` / region). SSR leaves a static fallback inside `[data-pb-lucide-live]`;
 * this component mounts the real `lucide-animated` components after load.
 */
export function LucideAnimatedPublicHydrator() {
	useEffect(() => {
		let cancelled = false;
		const roots = new Map<HTMLElement, Root>();

		void import("lucide-animated").then((mod) => {
			if (cancelled) {
				return;
			}
			const lib = mod as Record<string, ComponentType<unknown>>;
			const nodes = document.querySelectorAll<HTMLElement>(
				"[data-pb-lucide-live]"
			);
			for (const el of nodes) {
				if (el.getAttribute("data-pb-lucide-hydrated") === "1") {
					continue;
				}
				const props = readMountProps(el);
				const CmpRaw = lib[props.iconId];
				if (!isLikelyAnimatedIconExport(CmpRaw)) {
					continue;
				}
				const Cmp = CmpRaw as ComponentType<{
					size?: number;
					style?: CSSProperties;
					ref?: RefObject<AnimatedHandle | null>;
				}>;
				const preboundButtonOrLinkHost =
					props.delegateToParentButton && !props.hostId
						? (() => {
								const h = el.closest("a, button");
								return h instanceof HTMLElement ? h : null;
							})()
						: undefined;
				el.setAttribute("data-pb-lucide-hydrated", "1");
				const root = createRoot(el);
				roots.set(el, root);
				root.render(
					<HydratedLucideLive
						Cmp={Cmp}
						color={props.color}
						delegateToParentButton={props.delegateToParentButton}
						hostId={props.hostId}
						playOn={props.playOn}
						preboundButtonOrLinkHost={preboundButtonOrLinkHost}
						sizePx={props.sizePx}
						strokeW={props.strokeW}
					/>
				);
			}
		});

		return () => {
			cancelled = true;
			for (const [el, root] of roots) {
				try {
					root.unmount();
				} catch {
					// ignore
				}
				el.removeAttribute("data-pb-lucide-hydrated");
			}
			roots.clear();
		};
	}, []);

	return null;
}
